import { useState, useRef } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    CombineInputGroup,
    RupeeIcon,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import { MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import "react-dropdown/style.css";
import { FormControl } from "@mui/material";
import SelectOptionModal from "../SelectOptionModal";
import { staff_roles } from "../../Config";
import Config from "../../Config";

const StaffDetailModal = ({
    editStaffLoading,
    editStaffMutate,
    selectedData,
    setIsDeactivateStaff,
    apiData,
}) => {
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const formikRef = useRef(null);

    // Format staff_roles for SelectOptionModal
    const staffRoleOptions = staff_roles.map((role) => ({
        label: role.label,
        value: role.value,
    }));

    const gender = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
    ];

    const validationSchema = Yup.object({
        fullName: Yup.string()
            .required("Name is required")
            .matches(
                /^[A-Za-z0-9.\s]+$/,
                "Only letters, numbers, spaces and dot are allowed.",
            )
            .min(4, "Name must be at least 4 characters long"),
        mobile: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),
        staff: Yup.number().required("Staff Role is required"),
        gender: Yup.number().required("Gender is required"),
        commissionType: Yup.mixed().nullable(),
        commissionValue: Yup.number().when("commissionType", {
            is: 0,
            then: (schema) =>
                schema
                    .required("Commission value is required")
                    .min(0, "Amount cannot be negative")
                    .max(500000, "Flat commission cannot exceed ₹5,00,000"),
            otherwise: (schema) =>
                schema.when("commissionType", {
                    is: 1,
                    then: (schema) =>
                        schema
                            .required("Commission percentage is required")
                            .min(1, "Minimum percentage is 1%")
                            .max(100, "Percentage cannot exceed 100%"),
                    otherwise: (schema) => schema.notRequired(),
                }),
        }),
    });

    const initialValues = {
        fullName: apiData?.name || "",
        mobile: apiData?.mobile || "",
        staff: apiData?.role || "",
        gender: apiData?.gender || "",
        salary: apiData?.salary || "",
        commissionType: apiData?.commissionType,
        commissionValue: apiData?.commission || "",
        isOnPayroll: apiData?.isOnPayroll,
        isSuperAdmin: apiData?.isSuperAdmin || 0,
        canPunchOutAnywhere: apiData?.canPunchOutAnywhere || 0
    };

    const handleSubmit = (values) => {
        const nameRegex = /^[A-Za-z0-9. ]{4,}$/;
        const mobileRegex = /^\d{10}$/;
        const salaryRegex = /^\d{1,7}$/;
        const maxSalaryDeposit = 1000000;

        if (!nameRegex.test(values.fullName)) {
            toast.error("Only letters, numbers, spaces and dot are allowed.");
            return;
        }

        if (!mobileRegex.test(values.mobile)) {
            toast.error(
                "Mobile number should be exactly 10 digits long and contain only numbers",
            );
            return;
        }

        if (!values.gender) {
            toast.error("Gender is required");
            return;
        }

        if (!values.staff) {
            toast.error("Staff Role is required");
            return;
        }

        if (
            (values.salary && !salaryRegex.test(values.salary)) ||
            parseInt(values.salary) > maxSalaryDeposit
        ) {
            toast.error(
                "Salary must be less than 10,00,000 and contain only numbers",
            );
            return;
        }
        const payload = {
            name: values.fullName,
            mobile: values.mobile,
            role: values.staff,
            gender: values.gender,
            salary: Number(values.salary || 0),
            staffId: apiData?.id,
            isOnPayroll: values.isOnPayroll,
            isSuperAdmin: values.isSuperAdmin,
            canPunchOutAnywhere: values.canPunchOutAnywhere
        };

        if (values.commissionType !== null && values.commissionType !== undefined) {
            payload.commissionType = Number(values.commissionType);
            payload.commission = Number(values.commissionValue);
        }

        editStaffMutate(payload);

        // editStaffMutate({
        //     name: values.fullName,
        //     mobile: values.mobile,
        //     role: values.staff,
        //     gender: values.gender,
        //     salary: Number(values.salary || 0),
        //     staffId: apiData?.id,
        // });
    };

    const handleDeactivateStaff = () => {
        setIsDeactivateStaff(true);
    };

    const getInputClass = (isDisabled) => {
        return isDisabled
            ? "bg-gray-100 cursor-not-allowed"
            : "pl-2 relative rounded-md w-full flex items-center border border-gray-400 h-12";
    };
    const handleIncrementTypeChange = (type, setFieldValue) => {
        // Convert string to number or keep as null
        const numericType = type === "" ? null : Number(type);
        setFieldValue("commissionType", numericType);
        setFieldValue("commissionValue", "");
    };

    const handleIncrementValueChange = (value, setFieldValue) => {
        setFieldValue("commissionValue", value);
    };
    return (
        <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize={true}
        >
            {({
                values,
                setFieldValue,
                handleChange,
                handleBlur,
                errors,
                touched,
            }) => {
                const getCurrentRoleLabel = () => {
                    const selectedRole = staff_roles.find(
                        (role) => role.value === values?.staff,
                    );
                    return selectedRole ? selectedRole.label : "Select staff role";
                };

                const handleRoleSelect = (selectedValue) => {
                    setFieldValue("staff", selectedValue);
                    if (selectedValue !== Config.STAFF_ROLES.ADMIN) {
                        setFieldValue("isSuperAdmin", 0);
                    }
                };

                return (
                    <Form>
                        <Wrapper>
                            <CombineInputGroup>
                                {/* Full Name */}
                                <InputGroup>
                                    <Label htmlFor="fullName">Full Name *</Label>
                                    <FieldWrapper>
                                        <Field
                                            type="text"
                                            name="fullName"
                                            id="fullName"
                                            autoComplete="off"
                                            className={getInputClass(false)}
                                            placeholder="Enter full name"
                                        />
                                    </FieldWrapper>
                                    {errors.fullName && touched.fullName && (
                                        <ErrorText>{errors.fullName}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Mobile */}
                                <InputGroup>
                                    <Label htmlFor="mobile">Mobile *</Label>
                                    <FieldWrapper className="bg-gray-100">
                                        <Field
                                            type="text"
                                            name="mobile"
                                            id="mobile"
                                            autoComplete="off"
                                            maxLength={10}
                                            className={getInputClass(false)}
                                            placeholder="Enter 10-digit mobile number"
                                            readOnly
                                        />
                                    </FieldWrapper>
                                    {errors.mobile && touched.mobile && (
                                        <ErrorText>{errors.mobile}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Gender */}
                                <InputGroup>
                                    <Label>Gender *</Label>
                                    <FormControl fullWidth>
                                        <Select
                                            className="px-2"
                                            id="gender"
                                            name="gender"
                                            value={values.gender}
                                            onChange={(e) => setFieldValue("gender", e.target.value)}
                                            onBlur={handleBlur}
                                            style={{ height: "46px" }}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return (
                                                        <span className="text-gray-400">
                                                            Select gender preference
                                                        </span>
                                                    );
                                                }
                                                const option = gender.find(
                                                    (opt) => opt.value === selected,
                                                );
                                                return option ? option.label : selected;
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <span className="text-gray-400">
                                                    Select gender preference
                                                </span>
                                            </MenuItem>
                                            {gender.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {touched.gender && errors.gender && (
                                        <p className="text-rose-600 text-sm">{errors.gender}</p>
                                    )}
                                    {errors.gender && touched.gender && (
                                        <ErrorText>{errors.gender}</ErrorText>
                                    )}
                                </InputGroup>
                            </CombineInputGroup>

                            <CombineInputGroup>
                                {/* Staff Role with SelectOptionModal */}
                                <InputGroup>
                                    <Label htmlFor="staff">Staff Role *</Label>
                                    <FieldWrappers
                                        onClick={() => setIsRoleModalOpen(true)}
                                        className="border border-gray-400 cursor-pointer"
                                    >
                                        <Field
                                            type="text"
                                            name="staffDisplay"
                                            id="staffDisplay"
                                            autoComplete="off"
                                            readOnly
                                            value={getCurrentRoleLabel()}
                                            className="w-full border-none outline-none bg-transparent cursor-pointer pl-2"
                                            placeholder="Select staff role"
                                        />
                                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                    </FieldWrappers>
                                    {errors.staff && touched.staff && (
                                        <ErrorText>{errors.staff}</ErrorText>
                                    )}
                                </InputGroup>
                                {/* Monthly Salary */}
                                <InputGroup>
                                    <Label htmlFor="salary">Monthly Salary</Label>
                                    <FieldWrapper>
                                        <RupeeIcon />
                                        <Field
                                            type="number"
                                            name="salary"
                                            id="salary"
                                            autoComplete="off"
                                            className={getInputClass(false)}
                                            // className="w-full pl-5 border border-gray-400 rounded-md h-12"
                                            placeholder="Enter monthly salary"
                                            min="0"
                                        />
                                    </FieldWrapper>
                                </InputGroup>
                                <InputGroup>
                                    <Label htmlFor="isOnPayroll">
                                        Employment Type
                                    </Label>
                                    <FormControl fullWidth>
                                        <Select
                                            id="isOnPayroll"
                                            name="isOnPayroll"
                                            value={values.isOnPayroll ?? ""}
                                            onChange={(e) => setFieldValue("isOnPayroll", e.target.value)}
                                            displayEmpty
                                            style={{ height: "46px" }}
                                            className="px-2"
                                            renderValue={(selected) => {
                                                if (selected === null || selected === undefined || selected === "") {
                                                    return (
                                                        <span className="text-gray-400">
                                                            On Payroll
                                                        </span>
                                                    );
                                                }
                                                return selected === 1
                                                    ? "On Payroll"
                                                    : "Contract Staff";
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em className="text-gray-400" >None</em>
                                            </MenuItem>
                                            <MenuItem value={1}>On Payroll</MenuItem>
                                            <MenuItem value={2}>Contract Staff</MenuItem>
                                        </Select>
                                    </FormControl>
                                </InputGroup>
                            </CombineInputGroup>
                            <CombineInputGroup>
                                {/* is Super Admin */}
                                {/* <InputGroup>
                                    <Label htmlFor="commissionType">
                                        Super Admin
                                    </Label>
                                    <FormControl fullWidth>
                                        <Select
                                            id="isSuperAdmin"
                                            name="isSuperAdmin"
                                            value={values.isSuperAdmin ?? ""}
                                            onChange={(e) => setFieldValue("isSuperAdmin", e.target.value)}
                                            displayEmpty
                                            style={{ height: "46px" }}
                                            className="px-2"
                                            disabled={values.staff !== Config.STAFF_ROLES.ADMIN}
                                            renderValue={(selected) => {
                                                if (selected === null || selected === undefined || selected === "") {
                                                    return (
                                                        <span className="text-gray-400">
                                                            Select Super Admin Access
                                                        </span>
                                                    );
                                                }
                                                return selected === 1 ? "Yes, Make Super Admin" : "No";
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em className="text-gray-400">Select Super Admin Access</em>
                                            </MenuItem>
                                            <MenuItem value={0}>No</MenuItem>
                                            <MenuItem value={1}>Yes, Make Super Admin</MenuItem>
                                        </Select>
                                    </FormControl>
                                </InputGroup> */}
                                {/* Commission Type - Optional */}
                                <InputGroup>
                                    <Label htmlFor="commissionType">
                                        Commission Type
                                    </Label>
                                    <FormControl fullWidth>
                                        <Select
                                            id="commissionType"
                                            name="commissionType"
                                            value={values.commissionType ?? ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleIncrementTypeChange(value, setFieldValue);
                                            }}
                                            displayEmpty
                                            style={{ height: "46px" }}
                                            className="px-2"
                                            renderValue={(selected) => {
                                                if (selected === null || selected === undefined || selected === "") {
                                                    return (
                                                        <span className="text-gray-400">
                                                            Select commission type (Optional)
                                                        </span>
                                                    );
                                                }
                                                return selected === 0
                                                    ? "Flat Amount (₹)"
                                                    : "Percentage (%)";
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em className="text-gray-400" >None</em>
                                            </MenuItem>
                                            <MenuItem value={0}>Flat Amount (₹)</MenuItem>
                                            <MenuItem value={1}>Percentage (%)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </InputGroup>

                                {/* Commission Value - Only show if commission type is selected */}
                                {/* {values.commissionType !== null &&
                                    values.commissionType !== undefined &&
                                    values.commissionType !== "" && ( */}
                                <InputGroup>
                                    <Label htmlFor="commissionValue">
                                        {values.commissionType === 0
                                            ? "Flat Amount (₹)"
                                            : "Percentage (%)"}
                                    </Label>
                                    <FieldWrapper >
                                        {/* {values.commissionType === 0 && <RupeeIcon />} */}
                                        <Field
                                            type="number"
                                            name="commissionValue"
                                            id="commissionValue"
                                            placeholder={
                                                values.commissionType === 0
                                                    ? "Enter amount"
                                                    : "Enter %"
                                            }
                                            value={values.commissionValue}
                                            onChange={(e) =>
                                                handleIncrementValueChange(
                                                    e.target.value,
                                                    setFieldValue,
                                                )
                                            }
                                            // className={getInputClass(false)}
                                            className={`${getInputClass(false)} ${errors.commissionValue && touched.commissionValue
                                                ? "border-rose-500"
                                                : "border-gray-400"
                                                }`}
                                            // min="0"
                                            // max={
                                            //     values.commissionType === 1
                                            //         ? "100"
                                            //         : undefined
                                            // }
                                            // step="any"
                                            disabled={
                                                values.commissionType === null ||
                                                values.commissionType === undefined ||
                                                values.commissionType === ""
                                            }
                                        />
                                        {/* {values.commissionType === 1 && (
                                            <span className="absolute right-3 text-gray-500">
                                                %
                                            </span>
                                        )} */}
                                    </FieldWrapper>
                                    {/* {errors.commissionValue && touched.commissionValue && (
                                        <ErrorText>{errors.commissionValue}</ErrorText>
                                    )} */}
                                </InputGroup>
                                {/* )} */}
                            </CombineInputGroup>
                            <CombineInputGroup>
                                {/* is Super Admin */}
                                <InputGroup>
                                    <Label htmlFor="commissionType">
                                        Super Admin
                                    </Label>
                                    <FormControl fullWidth>
                                        <Select
                                            id="isSuperAdmin"
                                            name="isSuperAdmin"
                                            value={values.isSuperAdmin ?? ""}
                                            onChange={(e) => setFieldValue("isSuperAdmin", e.target.value)}
                                            displayEmpty
                                            style={{ height: "46px" }}
                                            className="px-2"
                                            disabled={values.staff !== Config.STAFF_ROLES.ADMIN}
                                            renderValue={(selected) => {
                                                if (selected === null || selected === undefined || selected === "") {
                                                    return (
                                                        <span className="text-gray-400">
                                                            Select Super Admin Access
                                                        </span>
                                                    );
                                                }
                                                return selected === 1 ? "Yes, Make Super Admin" : "No";
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em className="text-gray-400">Select Super Admin Access</em>
                                            </MenuItem>
                                            <MenuItem value={0}>No</MenuItem>
                                            <MenuItem value={1}>Yes, Make Super Admin</MenuItem>
                                        </Select>
                                    </FormControl>
                                </InputGroup>
                                <InputGroup>
                                    <Label htmlFor="attendance">
                                        Cross-Property Attendance
                                    </Label>
                                    <FormControl fullWidth>
                                        <Select
                                            id="canPunchOutAnywhere"
                                            name="canPunchOutAnywhere"
                                            value={values.canPunchOutAnywhere ?? ""}
                                            onChange={(e) => setFieldValue("canPunchOutAnywhere", e.target.value)}
                                            displayEmpty
                                            style={{ height: "46px" }}
                                            className="px-2"
                                            // disabled={values.staff !== Config.STAFF_ROLES.ADMIN}
                                            renderValue={(selected) => {
                                                if (selected === null || selected === undefined || selected === "") {
                                                    return (
                                                        <span className="text-gray-400">
                                                            Select Attendance Access
                                                        </span>
                                                    );
                                                }
                                                return selected === 1 ? "Yes" : "No";
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em className="text-gray-400">Select Attendance Access</em>
                                            </MenuItem>
                                            <MenuItem value={0}>No</MenuItem>
                                            <MenuItem value={1}>Yes</MenuItem>
                                        </Select>
                                    </FormControl>
                                </InputGroup>
                            </CombineInputGroup>
                        </Wrapper>

                        <BtnWrapper>
                            {/* <SideCancelBtn
                                type="button"
                                onClick={handleDeactivateStaff}
                                className={`${apiData?.status === 1
                                        ? "hover:bg-rose-500"
                                        : "hover:bg-green-500"
                                    }`}
                            >
                                {apiData?.status == 1 ? "Deactivate Staff" : "Activate Staff"}
                            </SideCancelBtn> */}
                            <SubmitBtn type="submit" disabled={editStaffLoading}>
                                {editStaffLoading ? "Updating..." : "Update"}
                            </SubmitBtn>
                        </BtnWrapper>

                        {/* Staff Role Selection Modal */}
                        <SelectOptionModal
                            isOpen={isRoleModalOpen}
                            onClose={() => setIsRoleModalOpen(false)}
                            title="Select Staff Role"
                            options={staffRoleOptions}
                            selectedValue={values.staff}
                            onSelect={handleRoleSelect}
                            searchPlaceholder="Search staff roles..."
                            width="1/3"
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const BtnWrapper = tw.div`w-full flex items-center justify-end mt-4 gap-4`;
const FieldWrapper = tw.div`w-full`;
const SubmitBtn = tw.button`
text-sm  w-24  md:w-20 h-7 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed  md:text-xs bg-primary-color hover:bg-hover-color text-white rounded-md shadow-md`;
const SideCancelBtn = tw.button`
text-sm h-7 grid place-items-center px-4 disabled:opacity-50 disabled:cursor-not-allowed  md:text-xs hover:bg-rose-500 text-gray-600 hover:text-white rounded-md`;
export default StaffDetailModal;
